import React, { useState, useRef, useEffect, useContext } from "react";
import Button from "../ui/button/Button";
import {
  ServicesInfoCategory,
  IServiceInfo,
  ServicesInfoComputedType,
  IServicesInfo,
} from "../../types/servicesInfo";
import { useTranslation } from "react-i18next";
import {
  IServicesContext,
  ServicesContext,
} from "../../context/servicesContext";
import { servicesService } from "../../api/servicesService/servicesService";
import { toast } from "react-toastify";

interface IServiceInfoComputed extends IServiceInfo {
  selected: boolean;
  statusCode?: number;
}

interface IAdditionalServicesComputed extends ServicesInfoCategory {
  selected: boolean;
  servicesInfo: IServiceInfoComputed[];
}

interface IMainServicesComputed extends ServicesInfoCategory {
  selected: boolean;
  servicesInfo: IServiceInfoComputed[];
}

interface ISidebarProps {
  isOpen?: boolean;
  callbackCloseSidebar: () => void;
  servicesInfoObj: ServicesInfoComputedType | null;
  setServiceInfo: React.Dispatch<React.SetStateAction<any>>;
}

const SidebarAddService: React.FC<ISidebarProps> = ({
  isOpen = false,
  callbackCloseSidebar,
  servicesInfoObj,
  setServiceInfo,
}) => {
  const { t } = useTranslation();

  const { services, setServices } = useContext(
    ServicesContext
  ) as IServicesContext;

  const [mainServicesComputed, setMainServicesComputed] = useState<
    null | IMainServicesComputed[]
  >(null);
  const [additionalServicesComputed, setAdditionalServicesComputed] = useState<
    null | IAdditionalServicesComputed[]
  >(null);
  const [expandedAdditionalServices, setExpandedAdditionalServices] = useState<
    Set<string>
  >(new Set()); // Tiene traccia di quali additionalServices con options sono espansi
  const [trackingValidationError, setTrackingValidationError] = useState(false);

  const [currentServiceType, setCurrentServiceType] = useState<
    "mainServices" | "additionalServices" | null
  >(null);

  const [loading, setLoading] = useState(false);
  const canGo = useRef(true);

  // Function to check GPS selection rules
  const canSelectGPSOption = (
    serviceInfo: IServiceInfoComputed,
    optionIndex: number,
    isSelecting: boolean
  ): boolean => {
    // Only apply GPS rules for GPS services - check if any option has GPS codes
    const hasGPSOptions = serviceInfo.options?.some(opt => 
      opt.key === 605530000 || opt.key === 605530001
    );
    
    if (!hasGPSOptions) {
      return true;
    }

    const currentOption = serviceInfo.options[optionIndex];
    const isCurrentOptionSelected = currentOption.selected;
    const isGPSTrattore = currentOption.key === 605530000;
    const isGPSSemirimorchio = currentOption.key === 605530001;
    
    // Find the other GPS option in the same service
    const otherGPSOption = serviceInfo.options.find(opt => 
      opt.key === (isGPSTrattore ? 605530001 : 605530000)
    );

    // If we're deselecting
    if (!isSelecting && isCurrentOptionSelected) {
      // Check if service is qualified
      const currentServiceStatus = services?.additionalServices?.find(s => s.id === serviceInfo.id)?.statusCode;
      if (currentServiceStatus === 605530003) {
        // If qualified, check if we have both GPS options selected
        const selectedGPSOptionsCount = serviceInfo.options.filter(opt => 
          opt.selected && (opt.key === 605530000 || opt.key === 605530001)
        ).length;
        
        // Can only deselect if we have more than one GPS option selected
        if (selectedGPSOptionsCount <= 1) {
          return false; // Cannot deselect - must maintain at least one GPS option
        }
      } else {
        // If not qualified, check if the other GPS option is in "to be evaluated" state
        if (otherGPSOption?.selected && currentServiceStatus === 605530001) {
          // If other GPS is in "to be evaluated", cannot deselect current GPS
          return false;
        }
      }
    }
    
    // If we're selecting
    if (isSelecting && !isCurrentOptionSelected) {
      // Always allow selection
      return true;
    }

    return true;
  };

  // Function to check if GPS option can be deselected
  const canDeselectGPSOption = (
    serviceInfo: IServiceInfoComputed,
    optionIndex: number
  ): boolean => {
    // Only apply GPS rules for GPS services - check if any option has GPS codes
    const hasGPSOptions = serviceInfo.options?.some(opt => 
      opt.key === 605530000 || opt.key === 605530001
    );
    
    if (!hasGPSOptions) {
      return true;
    }

    const currentOption = serviceInfo.options[optionIndex];
    const isGPSTrattore = currentOption.key === 605530000;
    const isGPSSemirimorchio = currentOption.key === 605530001;
    
    // Find the other GPS option in the same service
    const otherGPSOption = serviceInfo.options.find(opt => 
      opt.key === (isGPSTrattore ? 605530001 : 605530000)
    );

    // Check if current service is qualified
    const currentServiceStatus = services?.additionalServices?.find(s => s.id === serviceInfo.id)?.statusCode;
    if (currentServiceStatus === 605530003) {
      // Count selected GPS options
      const selectedGPSOptionsCount = serviceInfo.options.filter(opt => 
        opt.selected && (opt.key === 605530000 || opt.key === 605530001)
      ).length;
      
      // Can only deselect if we have more than one GPS option selected
      return selectedGPSOptionsCount > 1;
    } else {
      // If not qualified, check if other GPS is in "to be evaluated" state
      if (otherGPSOption?.selected && currentServiceStatus === 605530001) {
        return false; // Cannot deselect if other GPS is in "to be evaluated"
      }
    }

    return true;
  };

  useEffect(() => {
    if (isOpen && !!servicesInfoObj && !!services) {
      setMainServicesComputed(
        servicesInfoObj.mainServices.map((item) => ({
          type: item.type,
          selected: false,
          servicesInfo: item.servicesInfo.map((service) => ({
            ...service,
            selected: !!services.mainServices.find((s) => s.id === service.id), // Imposta true se già presente
          })),
        }))
      );
      setAdditionalServicesComputed(
        servicesInfoObj.additionalServices.map((item) => ({
          type: item.type,
          selected: false,
          servicesInfo: item.servicesInfo.map((service) => {
            const existingService = services.additionalServices.find(
              (s) => s.id === service.id
            );
            const isRejected = existingService?.status === "Rejected";

            return {
              ...service,
              selected: isRejected ? false : !!existingService,
              options: service.options
                ? service.options.map((option) => ({
                    ...option,
                    selected: isRejected
                      ? false
                      : !!existingService?.options?.some(
                          (o: any) => o === option.key
                        ),
                  }))
                : [],
            };
          }),
        }))
      );
    }
  }, [isOpen, servicesInfoObj, services]); // Ora si aggiorna ogni volta che `isOpen` cambia

  const selectServiceType = (
    index: number,
    type: "mainServices" | "additionalServices" | null
  ) => {
    const servicesTypeComputed =
      type === "mainServices"
        ? mainServicesComputed
        : additionalServicesComputed;
    if (!!servicesTypeComputed) {
      const copyServicesTypeComputed = [...servicesTypeComputed];
      copyServicesTypeComputed[index].selected =
        !copyServicesTypeComputed[index].selected;
      if (copyServicesTypeComputed[index].selected === false) {
        copyServicesTypeComputed[index].servicesInfo.map(
          (serviceInfo) => (serviceInfo.selected = false)
        );
      }

      if (type === "additionalServices") {
        setAdditionalServicesComputed(copyServicesTypeComputed);
      } else if (type === "mainServices") {
        setMainServicesComputed(copyServicesTypeComputed);
      }
    }
  };

  const toggleExpandAdditionalService = (serviceId: string) => {
    setExpandedAdditionalServices((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(serviceId)) {
        newSet.delete(serviceId);
      } else {
        newSet.add(serviceId);
      }
      return newSet;
    });
  };

  const selectServiceInfo = (
    typeIndex: number,
    serviceInfoIndex: number,
    type: "mainServices" | "additionalServices" | null,
    optionIndex?: number
  ) => {
    const servicesTypeComputed =
      type === "mainServices"
        ? mainServicesComputed
        : additionalServicesComputed;

    if (!!servicesTypeComputed) {
      const copyServicesComputed = [...servicesTypeComputed];

      const serviceInfo =
        copyServicesComputed[typeIndex].servicesInfo[serviceInfoIndex];

      if (
        serviceInfo.statusCode !== 605530001 &&
        serviceInfo.statusCode !== 605530002
      ) {
        if (
          serviceInfo &&
          Array.isArray(serviceInfo.options) &&
          serviceInfo.options.length > 0 &&
          optionIndex !== undefined
        ) {
          // Check if this is a GPS service and apply GPS rules
          const hasGPSOptions = serviceInfo.options.some(opt => 
            opt.key === 605530000 || opt.key === 605530001
          );
          
          if (hasGPSOptions) {
            const isSelecting = !serviceInfo.options[optionIndex].selected;
            if (canSelectGPSOption(serviceInfo, optionIndex, isSelecting)) {
              serviceInfo.options[optionIndex].selected = !serviceInfo.options[optionIndex].selected;
            }
          } else {
            // Se il servizio ha delle opzioni, selezioniamo l'opzione specifica
            serviceInfo.options[optionIndex].selected =
              !serviceInfo.options[optionIndex].selected;
          }
        } else {
          // Se il servizio non ha opzioni, selezioniamo direttamente il servizio
          serviceInfo.selected = !serviceInfo.selected;
        }
      }

      if (type === "additionalServices") {
        setAdditionalServicesComputed(copyServicesComputed);
      } else if (type === "mainServices") {
        setMainServicesComputed(copyServicesComputed);
      }
    }
  };

  const isServiceAlreadyUsed = (
    item:
      | {
          type: string;
          name: string;
          description: string;
          id: string;
          options?: { key: number; value: string; selected?: boolean }[];
        }
      | { key: number; value: string; selected?: boolean },
    type: "mainServices" | "additionalServices" | null
  ) => {
    if (type !== null) {
      // Se l'oggetto è una opzione (non ha 'id' ma ha 'key' e 'value')
      if ("key" in item && "value" in item) {
        // console.log('item',item)

        return item.selected === true;
      }

      // Se l'oggetto è un serviceInfo (ha 'id')
      const usedService = services?.[type].find(
        (service) => service.id === item.id
      );
      // console.log('usedService',usedService,usedService? true:false);
      return usedService ? true : false;
    }
    return false;
  };
  const addServices = async () => {
    if (services !== null) {
      // Raccolta dei servizi principali selezionati (solo quelli NON già esistenti)
      const selectedMainServices: string[] = [];
      mainServicesComputed?.forEach((service) => {
        service.servicesInfo.forEach((serviceInfo) => {
          if (serviceInfo.selected) {
            // Controlla se il servizio è già presente nei servizi esistenti
            const isAlreadyPresent = services.mainServices.some(
              (existingService) => existingService.id === serviceInfo.id
            );
            // Aggiungi solo se NON è già presente
            if (!isAlreadyPresent) {
              selectedMainServices.push(serviceInfo.id);
            }
          }
        });
      });

      // Raccolta degli additionalServices selezionati con le rispettive options (solo quelli nuovi o con nuove opzioni)
      const selectedAdditionalServices: { id: string; options: number[] }[] =
        [];
      additionalServicesComputed?.forEach((service) => {
        service.servicesInfo.forEach((serviceInfo) => {
          if (
            serviceInfo.selected ||
            serviceInfo.options.some((option) => option.selected)
          ) {
            // Trova il servizio esistente se presente
            const existingService = services.additionalServices.find(
              (existingService) => existingService.id === serviceInfo.id
            );

            // Se il servizio non esiste, aggiungilo completamente
            if (!existingService) {
              selectedAdditionalServices.push({
                id: serviceInfo.id,
                options: serviceInfo.options
                  .filter((option) => option.selected)
                  .map((option) => option.key),
              });
            } else {
              // Se il servizio esiste, controlla se ci sono nuove opzioni selezionate
              const selectedOptions = serviceInfo.options
                .filter((option) => option.selected)
                .map((option) => option.key);
              
              // Filtra solo le opzioni che non sono già presenti nel servizio esistente
              const newOptions = selectedOptions.filter(
                (optionKey) => !existingService.options?.includes(optionKey)
              );

              // Aggiungi il servizio solo se ci sono nuove opzioni
              if (newOptions.length > 0) {
                selectedAdditionalServices.push({
                  id: serviceInfo.id,
                  options: newOptions,
                });
              }
            }
          }
        });
      });

      // VALIDAZIONE PER TRACCIABILITÀ (solo se ci sono servizi già selezionati)
      const trackingServices = additionalServicesComputed?.find(
        (service) =>
          service.type === "Tracciabilità" || service.type === "Tracking"
      );

      if (trackingServices) {
        const hasSelectedTrackingServices = trackingServices.servicesInfo.some(
          (service) => service.selected
        );

        if (hasSelectedTrackingServices) {
          const isTrackingValid = trackingServices.servicesInfo.some(
            (service) =>
              service.statusCode !== 105810001 &&
              service.options.some((option) => option.selected)
          );

          if (!isTrackingValid) {
            // Mostra il messaggio di errore e espandi il tab Tracciabilità
            setTrackingValidationError(true);
            setCurrentServiceType("additionalServices");

            // Espande automaticamente il tab Tracciabilità
            setAdditionalServicesComputed(
              (prev) =>
                prev?.map((service) =>
                  service.type === "Tracciabilità"
                    ? { ...service, selected: true }
                    : service
                ) || null
            );

            return; // Blocca il salvataggio
          }
        }
      }

      // Costruzione del payload
      const data = {
        mainServices: selectedMainServices,
        additionalServices: selectedAdditionalServices,
      };

      // console.log('services',services.additionalServices)
      const filteredAdditionalServices = data.additionalServices.filter(
        (service) =>
          // Ignora il filtraggio per i servizi che hanno delle options
          service.options.length > 0 ||
          !services.additionalServices.some(
            (s) =>
              s.id === service.id &&
              (s.statusCode === 605530003 || s.statusCode === 605530001)
          )
      );

      const filteredData = {
        ...data,
        additionalServices: filteredAdditionalServices,
      };

      // console.log('Filtered Data:', filteredData);

      if (data.mainServices.length > 0 || data.additionalServices.length > 0) {
        setLoading(true);
        const response = await servicesService.addServices(filteredData);

        if (response.success) {
          // Reset dei servizi
          const responseGetServices = await servicesService.services();
          if (
            responseGetServices.success &&
            mainServicesComputed !== null &&
            additionalServicesComputed !== null
          ) {
            setServices(responseGetServices.responseBody);

            const resetMainServicesComputed = mainServicesComputed.map(
              (service) => ({
                ...service,
                servicesInfo: service.servicesInfo.map((serviceInfo) => ({
                  ...serviceInfo,
                  selected: false,
                })),
                selected: false,
              })
            );

            const resetAdditionalServicesComputed =
              additionalServicesComputed.map((service) => ({
                ...service,
                servicesInfo: service.servicesInfo.map((serviceInfo) => ({
                  ...serviceInfo,
                  selected: false,
                  options: serviceInfo.options.map((option) => ({
                    ...option,
                    selected: false,
                  })),
                })),
                selected: false,
              }));

            setMainServicesComputed(resetMainServicesComputed);
            setAdditionalServicesComputed(resetAdditionalServicesComputed);
            setTrackingValidationError(false);
            setCurrentServiceType(null);
            callbackCloseSidebar();
          } else {
            window.location.reload();
          }
        } else {
          console.log("Error adding services");
          toast.error(t("errors.errorAddingServices"));
        }

        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (
      isOpen === false &&
      mainServicesComputed !== null &&
      additionalServicesComputed !== null
    ) {
      const copyMainServicesComputed = [...mainServicesComputed];
      const resetMainServicesComputed = copyMainServicesComputed.map(
        (service) => {
          const copyServicesInfo = [...service.servicesInfo];
          const resetServicesInfo = copyServicesInfo.map((serviceInfo) => {
            return {
              ...serviceInfo,
              selected: false,
            };
          });

          return {
            ...service,
            servicesInfo: resetServicesInfo,
            selected: false,
          };
        }
      );

      const copyAdditionalServicesComputed = [...additionalServicesComputed];
      const resetAdditionalServicesComputed =
        copyAdditionalServicesComputed.map((service) => {
          const copyServicesInfo = [...service.servicesInfo];
          const resetServicesInfo = copyServicesInfo.map((serviceInfo) => {
            return {
              ...serviceInfo,
              selected: false,
            };
          });

          return {
            ...service,
            servicesInfo: resetServicesInfo,
            selected: false,
          };
        });

      setMainServicesComputed(resetMainServicesComputed);
      setAdditionalServicesComputed(resetAdditionalServicesComputed);
      updateAdditionalServicesComputed(services);
      updateServicesState(services);

      setCurrentServiceType(null);
    }
  }, [isOpen]);

  const updateAdditionalServicesComputed = async (services: any) => {
    setAdditionalServicesComputed((prevState) => {
      const safePrevState = prevState || []; // Assicura che prevState sia almeno un array vuoto
      return safePrevState.map((computedService: any) => {
        if (computedService.type === "Tracciabilità") {
          return {
            ...computedService,
            servicesInfo: computedService.servicesInfo.map((info: any) => {
              const matchingService = services.additionalServices?.find(
                (service: any) => service.id === info.id
              );

              if (matchingService) {
                return {
                  ...info,
                  status: matchingService.status,
                  statusCode: matchingService.statusCode,
                  options:
                    matchingService.options?.map((option: any) => ({
                      key: option,
                      selected:
                        info.options?.find((o: any) => o.key === option)
                          ?.selected || false,
                      value:
                        info.options?.find((o: any) => o.key === option)
                          ?.value || "",
                    })) || [],
                };
              }

              return info;
            }),
          };
        }

        return computedService;
      });
    });
  };
  const updateServicesState = (services: any) => {
    setServiceInfo((prevState: any) => {
      const safePrevState = prevState || {
        additionalServices: [],
        mainServices: [],
      };
      return {
        ...safePrevState,
        additionalServices: safePrevState.additionalServices.map(
          (serviceCategory: any) => {
            return {
              ...serviceCategory,
              servicesInfo: serviceCategory.servicesInfo.map((info: any) => {
                const matchingService = services.additionalServices?.find(
                  (service: any) => service.id === info.id
                );

                if (matchingService) {
                  // Creiamo un dizionario delle nuove opzioni per accesso rapido
                  const newOptionsMap = new Map(
                    (matchingService.options || []).map((optionKey: any) => [
                      optionKey,
                      optionKey,
                    ])
                  );

                  // Manteniamo tutte le opzioni esistenti e aggiorniamo solo quelle presenti in matchingService
                  const updatedOptions =
                    info.options?.map((existingOption: any) => ({
                      ...existingOption,
                      selected: newOptionsMap.has(existingOption.key)
                        ? true
                        : existingOption.selected, // Mantieni lo stato di selezione
                      value: existingOption.value || "", // Mantieni il valore se esiste
                    })) || [];

                  // Aggiungi nuove opzioni che non erano presenti prima
                  matchingService.options?.forEach((optionKey: any) => {
                    if (
                      !updatedOptions.some((opt: any) => opt.key === optionKey)
                    ) {
                      updatedOptions.push({
                        key: optionKey,
                        selected: true, // Nuova opzione aggiunta → selezionata di default
                        value: "", // Valore vuoto se non presente in `info.options`
                      });
                    }
                  });

                  return {
                    ...info,
                    status: matchingService.status,
                    statusCode: matchingService.statusCode,
                    options: updatedOptions, // Manteniamo sia le opzioni esistenti che quelle nuove
                  };
                }

                return info;
              }),
            };
          }
        ),
      };
    });
  };

  useEffect(() => {
    console.log("computedServices", additionalServicesComputed);
    // console.log("serviceInfo", servicesInfoObj);
  }, [additionalServicesComputed, servicesInfoObj]);

  return (
    <aside
      className={
        "flex flex-col transition z-10 fixed right-0 top-0 h-[100dvh] w-screen md:w-[520px] bg-white overflow-auto hide-scrollbar" +
        (isOpen === true
          ? " translate-x-0 sidebar-shadow"
          : " translate-x-[100%]")
      }
    >
      <div className="shrink-0 h-[88px] px-[30px] pt-[32px] pb-[24px] border-b flex justify-between items-center">
        <div className="text-lg font-bold leading-[21.6px]">
          {t("common.sidebars.addServices.heading")}
        </div>
        <div onClick={callbackCloseSidebar} className="cursor-pointer">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className="w-8 h-8"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </div>
      </div>

      {/* MAIN CONTENT OF SIDEBAR */}
      <div className="grow overflow-auto px-[30px] py-8">
        {currentServiceType === null && (
          <>
            <div
              onClick={() => setCurrentServiceType("mainServices")}
              className={
                "flex mr-5 items-center border bg-lightGrey mb-6 p-[10px] rounded-[10px] w-max cursor-pointer border-lightGrey"
              }
            >
              <div className="font-bold text-[15px] leading-[19px] mr-[14px]">
                {t("screens.services.tabMainServices")}
              </div>
              <div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  className="w-4 h-4 stroke-red"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M8.25 4.5l7.5 7.5-7.5 7.5"
                  />
                </svg>
              </div>
            </div>

            <div
              onClick={() => setCurrentServiceType("additionalServices")}
              className={
                "flex mr-5 items-center border bg-lightGrey mb-6 p-[10px] rounded-[10px] w-max cursor-pointer border-lightGrey"
              }
            >
              <div className="font-bold text-[15px] leading-[19px] mr-[14px]">
                {t("screens.services.tabAdditionalServices")}
              </div>
              <div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  className="w-4 h-4 stroke-red"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M8.25 4.5l7.5 7.5-7.5 7.5"
                  />
                </svg>
              </div>
            </div>
          </>
        )}

        {currentServiceType !== null && (
          <>
            <div
              className="cursor-pointer uppercase font-bold text-sm mb-6 flex items-center"
              onClick={() => setCurrentServiceType(null)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="w-4 h-4 stroke-red"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15.75 19.5L8.25 12l7.5-7.5"
                />
              </svg>

              <div className="ml-2">
                {t("common.sidebars.addServices.goBack")}
              </div>
            </div>

            {!!servicesInfoObj &&
              !!mainServicesComputed &&
              currentServiceType === "mainServices" && (
                <>
                  {mainServicesComputed.map((item, index) => {
                    return (
                      <div key={index} className="flex items-start select-none">
                        <div
                          onClick={() =>
                            selectServiceType(index, currentServiceType)
                          }
                          className={
                            "flex mr-5 items-center border bg-lightGrey mb-6 p-[10px] rounded-[10px] w-max cursor-pointer" +
                            (item.selected
                              ? " border-red"
                              : " border-lightGrey")
                          }
                        >
                          <div className="font-bold text-[15px] leading-[19px] mr-[14px]">
                            {item.type}
                          </div>
                          <div>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={2}
                              className="w-4 h-4 stroke-red"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M8.25 4.5l7.5 7.5-7.5 7.5"
                              />
                            </svg>
                          </div>
                        </div>

                        <div className={item.selected ? "block" : "hidden"}>
                          {item.servicesInfo.map((serviceInfo, i) => {
                            return (
                              <div
                                onClick={() =>
                                  selectServiceInfo(index, i, "mainServices")
                                }
                                key={i}
                                className={
                                  "group bg-lightGrey relative mb-6 p-[10px] rounded-[10px] w-max cursor-pointer text-[15px] leading-[19px]" +
                                  (isServiceAlreadyUsed(
                                    serviceInfo,
                                    "mainServices"
                                  ) === true
                                    ? " bg-red text-white selected-serviceinfo"
                                    : serviceInfo.selected
                                    ? " bg-red text-white"
                                    : "")
                                }
                              >
                                {serviceInfo.name}

                                {!!serviceInfo.description && (
                                  <div className="hidden sm:group-hover:block w-max max-w-[150px] z-10 absolute top-[50%] -translate-y-[50%] left-[calc(100%+15px)] p-4 bg-white rounded-[8px] shadow text-[#6F6F6F] text-sm">
                                    {serviceInfo.description}

                                    <div className="absolute -left-[9px] top-[50%] -translate-y-[50%] w-0 h-0 border-t-[9px] border-t-transparent border-r-[9px] border-[#edededb3] border-b-[9px] border-b-transparent"></div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </>
              )}

            {!!servicesInfoObj &&
              !!additionalServicesComputed &&
              currentServiceType === "additionalServices" &&  (
                <>
                  {additionalServicesComputed?.map((item, index) => (
                    <div key={index} className="flex items-start select-none">
                      {/* HEADER DELLA CATEGORIA ADDITIONAL SERVICE */}
                      <div
                        onClick={() =>
                          selectServiceType(index, "additionalServices")
                        }
                        className={`flex mr-5 items-center border bg-lightGrey mb-6 p-[10px] rounded-[10px] w-max cursor-pointer ${
                          item.selected ? "border-red" : "border-lightGrey"
                        }`}
                      >
                        <div className="font-bold text-[15px] leading-[19px] mr-[14px]">
                          {item.type}
                        </div>
                        <div>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={2}
                            className="w-4 h-4 stroke-red"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M8.25 4.5l7.5 7.5-7.5 7.5"
                            />
                          </svg>
                        </div>
                      </div>

                      {/* CONTENUTO DELLA CATEGORIA ADDITIONAL SERVICE */}
                      <div className={item.selected ? "block" : "hidden"}>
                        {/* Mostra il messaggio di errore se necessario */}
                        {trackingValidationError &&
                          item.type === "Tracciabilità" && (
                            <div className="text-red text-xs mb-2 ml-1">
                              Selezionare almeno un'opzione
                            </div>
                          )}
                        {item.servicesInfo.map((serviceInfo, i) => (
                          <div key={i} className="relative">
                            {/* SE UN SERVIZIO HA OPTIONS, VIENE TRATTATO COME CATEGORIA */}
                            {serviceInfo.options.length > 0 ? (
                              <div
                              
                                onClick={() =>
                                  toggleExpandAdditionalService(serviceInfo.id)
                                }
                                className={`flex mr-5 items-center border bg-lightGrey mb-6 p-[10px] rounded-[10px] w-max cursor-pointer 
                                                                    ${
                                                                      expandedAdditionalServices.has(
                                                                        serviceInfo.id
                                                                      )
                                                                        ? "border-red"
                                                                        : "border-lightGrey"
                                                                    }`}
                              >
                                <div className="font-bold text-[15px] leading-[19px] mr-[14px]">
                                  {serviceInfo.name}
                                </div>
                                <div>
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth={2}
                                    className="w-4 h-4 stroke-red"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M8.25 4.5l7.5 7.5-7.5 7.5"
                                    />
                                  </svg>
                                </div>
                              </div>
                            ) : (
                              // SE UN SERVIZIO NON HA OPTIONS, È UN ELEMENTO CLICCABILE DIRETTO
                              <div
                                onClick={() => {
                                  if (
                                    !isServiceAlreadyUsed(
                                      serviceInfo,
                                      "additionalServices"
                                    ) ||
                                    !serviceInfo.selected
                                  ) {
                                    selectServiceInfo(
                                      index,
                                      i,
                                      "additionalServices"
                                    );
                                  }
                                }}
                                className={`group bg-lightGrey relative mb-6 p-[10px] rounded-[10px] w-max cursor-pointer text-[15px] leading-[19px] 
                                                                    ${
                                                                      isServiceAlreadyUsed(
                                                                        serviceInfo,
                                                                        "additionalServices"
                                                                      ) &&
                                                                      serviceInfo.selected
                                                                        ? "bg-red text-white selected-serviceinfo"
                                                                        : isServiceAlreadyUsed(
                                                                            serviceInfo,
                                                                            "additionalServices"
                                                                          ) &&
                                                                          !serviceInfo.selected
                                                                        ? ""
                                                                        : serviceInfo.selected
                                                                        ? "bg-red text-white selected-serviceinfo"
                                                                        : ""
                                                                    }`}
                              >
                                {serviceInfo.name}
                              </div>
                            )}

                            {/* SE ESPANSO, MOSTRA LE OPTIONS COME SERVICEINFO NORMALI */}

                            {expandedAdditionalServices.has(serviceInfo.id) &&
                              serviceInfo.options.length > 0 && (
                                <div className="ml-6">
                                  {serviceInfo.options.map(
                                    (option, optionIndex) => {
                                      // Check if this is a GPS service
                                      const isGPSService = option.key === 605530000 || option.key === 605530001;
                                      
                                      // For GPS services, use the GPS selection rules
                                      let canDeselect = true;
                                      if (isGPSService) {
                                        canDeselect = canDeselectGPSOption(serviceInfo, optionIndex);
                                      } else {
                                        // For non-GPS services, use the existing logic
                                        const selectedOptionsCount =
                                          serviceInfo.options.filter(
                                            (opt) => opt.selected
                                          ).length;
                                        const isQualified =
                                          serviceInfo.statusCode === 605530003;
                                        canDeselect =
                                          !isQualified ||
                                          selectedOptionsCount > 1 ||
                                          !option.selected;
                                      }

                                      return (
                                        <div
                                          key={optionIndex}
                                          onClick={() => {
                                            if (
                                              canDeselect ||
                                              !option.selected
                                            ) {
                                              selectServiceInfo(
                                                index,
                                                i,
                                                "additionalServices",
                                                optionIndex
                                              );
                                            }
                                          }}
                                          className={`group bg-lightGrey relative mb-3 p-[10px] rounded-[10px] w-max cursor-pointer text-[15px] leading-[19px]
              ${
                isServiceAlreadyUsed(option, "additionalServices") &&
                option.selected
                  ? "bg-red text-white selected-serviceinfo"
                  : isServiceAlreadyUsed(option, "additionalServices") &&
                    !option.selected
                  ? ""
                  : option.selected
                  ? "bg-red text-white selected-serviceinfo"
                  : ""
              }
              ${
                !canDeselect && option.selected
                  ? "cursor-not-allowed opacity-50"
                  : ""
              }`}
                                        >
                                          {option.value}
                                        </div>
                                      );
                                    }
                                  )}
                                </div>
                              )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </>
              )}
          </>
        )}
      </div>

      <div className="shrink-0 py-6 px-[30px] text-right border-t">
        <Button callback={addServices} loading={loading}>
          {t("common.sidebars.saveButtonText")}
        </Button>
      </div>
    </aside>
  );
};

export default React.memo(SidebarAddService);
